// TableModals.jsx

import React from 'react';

// Utilities
import { resolveFieldValue } from 'views/table/utils/tableFieldUtils';

// Components
import MemberPickerModal from 'components/memberpicker/MemberPickerModal';
import FieldModal from 'node/form/fieldmodal/FieldModal';
import NodeDateModal from 'node/form/fields/date/NodeDateModal';
import NodeDateRangeModal from 'node/form/fields/daterange/NodeDateRangeModal';
import NodeDateTimeModal from 'node/form/fields/datetime/NodeDateTimeModal';
import NodeDateTimeRangeModal from 'node/form/fields/datetimerange/NodeDateTimeRangeModal';
import NodeLocationModal from 'node/form/fields/location/NodeLocationModal';
import NodeMultiselectModal from 'node/form/fields/multiselect/NodeMultiselectModal';
import NodePercentageModal from 'node/form/fields/percentage/NodePercentageModal';
import NodeNodelistModal from 'node/form/fields/nodelist/NodeNodelistModal';
import NodeNodelistSingleModal from 'node/form/fields/nodelist/NodeNodelistSingleModal';
import NodeSelectModal from 'node/form/fields/select/NodeSelectModal';
import NodeTagsModal from 'node/form/fields/tags/NodeTagsModal';
import { isNodelistAllowMultiple } from 'utilities/NodelistField';

/**
 * TableModals
 *
 * Hosts table cell editor modals and routes modal callbacks through
 * shared table handlers.
 * 
 * @param {object} modalState - The state object for modals, containing type, node, fieldDef, etc.
 * @param {string} fieldDataSection - The section of the node data to resolve field values from (e.g. 'properties', 'metadata').
 * @param {function} handleModalClose - Callback function to call when any modal is closed.
 * @param {function} handleEnumChange - Callback function to call when an enum field value is changed, called with (newValue).
 * @param {function} handleDateChange - Callback function to call when a date field value is changed, called with (newValue).
 * @param {function} handleTagsSaved - Callback function to call when tags are saved in the tags modal, called with (newTagsArray).
 * @param {function} handleLocationSaved - Callback function to call when a location is saved in the location modal, called with (newLocationValue).
 * @param {function} handlePeopleSaved - Callback function to call when people are saved in the people modal, called with (newPeopleArray).
 * @param {function} handlePercentageChange - Callback function to call when percentage is saved in the percentage modal, called with (newPercentage).
 * @param {boolean} fieldModalOpen - Flag indicating if the <fieldset></fieldset> modal is open.
 * @param {function} handleFieldModalOpen - Callback function to call when the field modal is opened or closed, called with (isOpen).
 * @param {function} handleFieldSave - Callback function to call when a field is saved in the field modal, called with (newValue).
 * @param {boolean} fieldSaving - Flag indicating if a field is currently being saved.
 *
 * @returns {ReactNode} TableModals.
 */
const TableModals = ({
    modalState,
    fieldDataSection,
    handleModalClose,
    handleEnumChange,
    handleDateChange,
    handleTagsSaved,
    handleLocationSaved,
    handlePeopleSaved,
    handleNodelistSaved,
    handlePercentageChange,
    fieldModalOpen,
    handleFieldModalOpen,
    handleFieldSave,
    fieldSaving,
}) => {

  // ----- RENDER ----- //

  return (
    <>
        <NodeSelectModal
          node={modalState.node}
          label={modalState.fieldDef?.label || modalState.fieldDef?.name || 'Select'}
          fieldName={modalState.fieldDef?.name || ''}
          value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection) || ''}
          isOpen={modalState.type === 'enum' || modalState.type === 'pipeline'}
          setOpen={(open) => {
                if (!open) handleModalClose();
            }}
          onChange={handleEnumChange}
        />

        <NodeMultiselectModal
          node={modalState.node}
          label={modalState.fieldDef?.label || modalState.fieldDef?.name || 'Multi-Select'}
          fieldName={modalState.fieldDef?.name || ''}
          value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection) || []}
          isOpen={modalState.type === 'multiselect'}
          setOpen={(open) => {
                if (!open) handleModalClose();
            }}
          onChange={handleEnumChange}
        />

        <NodeDateModal
          isOpen={modalState.type === 'date'}
          setOpen={(open) => {
                if (!open) handleModalClose();
            }}
          value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection)}
          onChange={handleDateChange}
        />

        <NodeDateRangeModal
          isOpen={modalState.type === 'daterange'}
          setOpen={(open) => {
                if (!open) handleModalClose();
            }}
          value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection)}
          onChange={handleDateChange}
        />

        <NodeDateTimeModal
          isOpen={modalState.type === 'datetime'}
          setOpen={(open) => {
                if (!open) handleModalClose();
            }}
          value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection)}
          onChange={handleDateChange}
        />

        <NodeDateTimeRangeModal
          isOpen={modalState.type === 'datetimerange'}
          setOpen={(open) => {
                if (!open) handleModalClose();
            }}
          value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection)}
          onChange={handleDateChange}
        />

        <NodeTagsModal
          node={modalState.node}
          isOpen={modalState.type === 'tags'}
          setOpen={(open) => {
                if (!open) handleModalClose();
            }}
          onTagsSaved={handleTagsSaved}
        />

        <NodePercentageModal
          label={modalState.fieldDef?.label || modalState.fieldDef?.name || 'Percentage'}
          value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection)}
          snapTo={modalState.fieldDef?.snapTo}
          isOpen={modalState.type === 'percentage'}
          setOpen={(open) => {
                if (!open) handleModalClose();
            }}
          onChange={handlePercentageChange}
        />

        <NodeLocationModal
          value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection) || null}
          isOpen={modalState.type === 'location'}
          setOpen={(open) => {
                if (!open) handleModalClose();
            }}
          onLocationSaved={handleLocationSaved}
        />

        <MemberPickerModal
          node={modalState.node}
          label={modalState.fieldDef?.label || modalState.fieldDef?.name || 'People'}
          value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection) || []}
          isOpen={modalState.type === 'people'}
          setOpen={(open) => {
                if (!open) handleModalClose();
            }}
          onChange={handlePeopleSaved}
        />

        {isNodelistAllowMultiple(modalState.fieldDef) ? (
          <NodeNodelistModal
            fieldLabel={modalState.fieldDef?.label || modalState.fieldDef?.name || 'Relation'}
            isOpen={modalState.type === 'relation'}
            node={modalState.node}
            setOpen={(open) => {
              if (!open) handleModalClose();
            }}
            targetType={String(modalState.fieldDef?.targetType || '').trim()}
            displayFields={modalState.fieldDef?.displayFields}
            value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection) || []}
            onChange={handleNodelistSaved}
          />
        ) : (
          <NodeNodelistSingleModal
            fieldLabel={modalState.fieldDef?.label || modalState.fieldDef?.name || 'Related Item'}
            isOpen={modalState.type === 'relation'}
            node={modalState.node}
            setOpen={(open) => {
              if (!open) handleModalClose();
            }}
            showFilter={modalState.fieldDef?.showFilter === true}
            targetType={String(modalState.fieldDef?.targetType || '').trim()}
            value={resolveFieldValue(modalState.node, modalState.fieldDef, fieldDataSection) || []}
            onChange={handleNodelistSaved}
          />
        )}

        <FieldModal
          isOpen={fieldModalOpen}
          setOpen={handleFieldModalOpen}
          isSaving={fieldSaving}
          onSave={handleFieldSave}
        />
    </>
);
};

// Dev Tools
TableModals.displayName = 'TableModals';

export default TableModals;
